<template>
  <v-container fluid class="list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editCurriculum="selectedCurriculum"
    />
    <AssignSubject
      :editDialog="assignSubjectDialog"
      :selectedCurriculum="selectedCurriculum"
      @close="assignSubjectClose"
      @submit="assigntSubjectSubmit"
    />

    <div class="list-page-heading">
      <div><div class="list-page-heading__eyebrow">Report cards</div><h1>Curriculums</h1><p>Build reusable subject and marks structures for report cards.</p></div>
      <div class="list-page-heading__actions"><v-btn v-if="checkRightStatus(45)" color="primary" depressed @click="add"><v-icon left>mdi-plus</v-icon>New curriculum</v-btn></div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-bookshelf</v-icon></div><div><div class="list-metric__value">{{ filteredCurriculums.length }}</div><div class="list-metric__label">Curriculums in result</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-book-open-variant</v-icon></div><div><div class="list-metric__value">{{ totalSubjects }}</div><div class="list-metric__label">Assigned subjects</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-counter</v-icon></div><div><div class="list-metric__value">{{ totalMarks }}</div><div class="list-metric__label">Combined marks</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-book-plus-outline</v-icon></div><div><div class="list-metric__value">{{ emptyCurriculumCount }}</div><div class="list-metric__label">Need subjects</div></div></div></v-col>
    </v-row>

    <v-card :loading="loading" elevation="0" class="list-card">
      <div class="list-toolbar">
        <v-text-field v-model="search" class="list-toolbar__search" outlined prepend-inner-icon="mdi-magnify" dense background-color="#F8FAF8" placeholder="Search curriculums or assigned subjects" single-line hide-details clearable />
        <div class="list-toolbar__actions"><v-btn icon outlined color="primary" :loading="loading" aria-label="Refresh curriculums" @click="getList"><v-icon size="19">mdi-refresh</v-icon></v-btn></div>
      </div>

      <div class="list-groups">
        <v-expansion-panels v-if="filteredCurriculums.length" multiple flat class="list-group-panels">
          <v-expansion-panel
            v-for="curriculum in filteredCurriculums"
            :key="curriculum.id"
          >
            <v-expansion-panel-header class="list-group-header">
              <div class="list-group-heading">
                <div class="list-group-heading__icon"><v-icon color="primary" size="20">mdi-bookshelf</v-icon></div>
                <div class="list-group-heading__copy">
                  <strong class="text-capitalize">{{ curriculum.name }}</strong>
                  <span>{{ getSubjectCount(curriculum) ? `${getSubjectCount(curriculum)} subjects · ${getTotalMarks(curriculum)} total marks` : "No subjects assigned yet" }}</span>
                </div>
                <div class="list-group-heading__actions">
                  <v-chip small label color="green lighten-5" text-color="green darken-2">
                    {{ getSubjectCount(curriculum) }} Subject{{ getSubjectCount(curriculum) > 1 ? "s" : "" }}
                  </v-chip>
                  <v-btn
                    small outlined color="primary"
                    @click.stop="assignSubject(curriculum)"
                  >
                    <v-icon left small>mdi-book-plus-outline</v-icon>Assign subject
                  </v-btn>

                  <v-menu bottom left offset-y @click.stop="">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        @click.stop
                      >
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </template>

                    <v-list class="menu-button">
                      <v-list-item @click="assignSubject(curriculum)">
                        <v-list-item-title>Assign Subject</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        v-if="checkRightStatus(47)"
                        @click="editCurriculum(curriculum)"
                      >
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>

                      <v-divider></v-divider>

                      <v-list-item
                        v-if="checkRightStatus(48)"
                        @click="deleteCurriculum(curriculum.id)"
                      >
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div
                v-if="
                  curriculum.subjectMeta &&
                  curriculum.subjectMeta.subjects &&
                  curriculum.subjectMeta.subjects.length
                "
              >
                <v-simple-table class="list-nested-table curriculum-subject-table">
                  <thead>
                    <tr>
                      <th class="text-left">Order</th>
                      <th class="text-left">Subject</th>
                      <th class="text-right">Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(subject, index) in getSortedSubjects(curriculum)"
                      :key="subject.id + '-' + index"
                    >
                      <td><span class="curriculum-order">{{ subject.order || index + 1 }}</span></td>
                      <td><div class="list-identity"><div class="list-identity__icon"><v-icon color="primary" size="18">mdi-book-outline</v-icon></div><div class="list-identity__content"><div class="list-identity__title text-capitalize">{{ subject.name }}</div></div></div></td>
                      <td class="text-right"><strong class="curriculum-marks">{{ subject.marks }}</strong></td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div v-else class="list-empty-inline">
                <div class="list-empty-inline__message"><v-icon color="grey lighten-1">mdi-book-plus-outline</v-icon><span>No subjects are assigned to this curriculum.</span></div>
                <v-btn small outlined color="primary" @click="assignSubject(curriculum)"><v-icon left small>mdi-book-plus-outline</v-icon>Assign first subject</v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-else class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-bookshelf</v-icon><strong>No curriculums found</strong><span>{{ search ? "Try a different curriculum or subject name." : "Create a curriculum and begin assigning subjects." }}</span></div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import curriculumService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");
const AssignSubject = () =>
  import("@/modules/ReportCard/Curriculums/components/AssignSubject.vue");

export default {
  name: "curriculum-list",
  components: {
    Create,
    Edit,
    AssignSubject,
  },
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCurriculum: null,
      assignSubjectDialog: false,
      curriculums: [],
      search: null,
      loading: true,
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Curriculum List";
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    filteredCurriculums() {
      if (!this.search) return this.curriculums;

      const search = this.search.toLowerCase();

      return this.curriculums.filter((curriculum) => {
        const curriculumName = curriculum.name
          ? curriculum.name.toLowerCase()
          : "";

        const subjectNames =
          curriculum.subjectMeta &&
          curriculum.subjectMeta.subjects &&
          curriculum.subjectMeta.subjects.length
            ? curriculum.subjectMeta.subjects
                .map((subject) => (subject.name ? subject.name.toLowerCase() : ""))
                .join(" ")
            : "";

        return (
          curriculumName.includes(search) || subjectNames.includes(search)
        );
      });
    },

    totalSubjects() {
      return this.filteredCurriculums.reduce((total, curriculum) => {
        return total + this.getSubjectCount(curriculum);
      }, 0);
    },

    totalMarks() {
      return this.filteredCurriculums.reduce((total, curriculum) => total + this.getTotalMarks(curriculum), 0);
    },

    emptyCurriculumCount() {
      return this.filteredCurriculums.filter((curriculum) => !this.getSubjectCount(curriculum)).length;
    },
  },
  methods: {
    add() {
      this.createDialog = true;
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      try {
        const response = await curriculumService.getAll();
        this.curriculums = response.data || [];
        return response;
      } finally {
        this.loading = false;
      }
    },

    getSubjectCount(curriculum) {
      if (
        curriculum &&
        curriculum.subjectMeta &&
        curriculum.subjectMeta.subjects
      ) {
        return curriculum.subjectMeta.subjects.length;
      }
      return 0;
    },

    getTotalMarks(curriculum) {
      if (
        !curriculum ||
        !curriculum.subjectMeta ||
        !curriculum.subjectMeta.subjects
      ) {
        return 0;
      }

      return curriculum.subjectMeta.subjects.reduce((total, subject) => {
        return total + (parseInt(subject.marks) || 0);
      }, 0);
    },

    getSortedSubjects(curriculum) {
      if (
        !curriculum ||
        !curriculum.subjectMeta ||
        !curriculum.subjectMeta.subjects
      ) {
        return [];
      }

      return [...curriculum.subjectMeta.subjects].sort((a, b) => {
        const orderA = a.order || 9999;
        const orderB = b.order || 9999;
        return orderA - orderB;
      });
    },

    editCurriculum(curriculum) {
      this.selectedCurriculum = curriculum;
      this.editDialog = true;
    },

    deleteCurriculum(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          curriculumService.delete(id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Curriculum has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },

    assignSubject(curriculum) {
      this.selectedCurriculum = curriculum;
      this.assignSubjectDialog = true;
    },

    assignSubjectClose() {
      this.selectedCurriculum = null;
      this.assignSubjectDialog = false;
    },

    assigntSubjectSubmit() {
      this.assignSubjectDialog = false;
      this.selectedCurriculum = null;
      this.getList();
    },
  },
};
</script>

<style scoped>
.curriculum-order {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #2e7d32;
  background: #edf7ee;
  font-weight: 750;
}

.curriculum-marks {
  color: #273247;
  font-size: 14px;
}

</style>
